import type { FC } from 'react';

import { Divider } from '../divider';
import { DrawerBackdrop } from './drawer-backdrop';
import { DrawerBody } from './drawer-body';
import { DrawerClose } from './drawer-close';
import { DrawerCloseButton } from './drawer-close-button';
import { DrawerContent } from './drawer-content';
import { DrawerFooter } from './drawer-footer';
import { DrawerHeader } from './drawer-header';
import { DrawerPortal } from './drawer-portal';
import { DrawerRoot } from './drawer-root';
import { DrawerTrigger } from './drawer-trigger';
import type { DrawerPropsInterface } from './types/drawer.types';

/**
 * A drawer dialog that slides in from the edge of the viewport, or of the
 * element passed to `container`.
 *
 * @since 10.22.0
 */
export const Drawer: FC<DrawerPropsInterface> & {
  Root: typeof DrawerRoot;
  Trigger: typeof DrawerTrigger;
  Portal: typeof DrawerPortal;
  Backdrop: typeof DrawerBackdrop;
  Content: typeof DrawerContent;
  Header: typeof DrawerHeader;
  Body: typeof DrawerBody;
  Footer: typeof DrawerFooter;
  CloseButton: typeof DrawerCloseButton;
  Close: typeof DrawerClose;
} = (props: DrawerPropsInterface) => {
  const {
    children,
    dividers = true,
    trigger,
    header,
    body,
    footer,
    closeButton,
    css: cssProp,
    style,
    className,
    portalId,
    portalRoot,
    portalPreserveTabOrder,
    scrim,
    ...rest
  } = props;
  return (
    <Drawer.Root {...rest}>
      <Drawer.Trigger>{trigger}</Drawer.Trigger>
      <Drawer.Portal id={portalId} root={portalRoot} preserveTabOrder={portalPreserveTabOrder}>
        <Drawer.Backdrop scrim={scrim}>
          <Drawer.Content css={cssProp} style={style} className={className}>
            <Drawer.CloseButton>{closeButton}</Drawer.CloseButton>
            {header ? <Drawer.Header>{header}</Drawer.Header> : null}
            {dividers ? <Divider /> : null}
            {body ? <Drawer.Body>{body}</Drawer.Body> : null}
            {children}
            {footer && dividers ? <Divider /> : null}
            {footer ? <Drawer.Footer>{footer}</Drawer.Footer> : null}
          </Drawer.Content>
        </Drawer.Backdrop>
      </Drawer.Portal>
    </Drawer.Root>
  );
};

Drawer.displayName = 'Drawer';

Drawer.Root = DrawerRoot;
Drawer.Trigger = DrawerTrigger;
Drawer.Portal = DrawerPortal;
Drawer.Backdrop = DrawerBackdrop;
Drawer.Content = DrawerContent;
Drawer.Header = DrawerHeader;
Drawer.Body = DrawerBody;
Drawer.Footer = DrawerFooter;
Drawer.CloseButton = DrawerCloseButton;
Drawer.Close = DrawerClose;
